import { FC, lazy, memo, ReactNode, Suspense, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { createHtmlPortalNode, InPortal } from 'react-reverse-portal'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import { tokens } from '@getgo/chameleon-core/next/goto-admin'
import { Box, makeStyles, Theme } from '@material-ui/core'
import useLocalStorage from '@rehooks/local-storage'
import { useMessaging } from '@ucc/admin-messaging'
import {
  Analytics,
  BILLING_ROUTE,
  DEVICES_ROUTE,
  GTC_BILLING_ROUTE,
  PEOPLE_ROUTE,
  PHONES_ROUTE,
  PLACES_ROUTE,
  SETTINGS_ROUTE,
  usePrivilege,
} from '@ucc/unified-admin-common'
import clsx from 'clsx'

import {
  AdminBanners,
  OrganizationContext,
  Sidebar,
  SmallSidebar,
  TopBar,
  TopBarBannerContext,
  TopPageLoader,
} from 'components'
import config from 'config'
import { useActivities, useBanners, useHideSplashScreen, useLocale, useResolveUpgradeBanner } from 'hooks'
import { CopilotLegalTermsStatus } from 'types/CopilotLegalTerms'
import { SetupType } from 'types/DynamicSetupConfig'
import { SidebarLink } from 'types/SidebarLink'
import { mixpanelEventNames, redirect } from 'utils'
import { calculateBannerHeight, calculateMargin } from 'utils/bannerMargin'
import { SHOW_ADMIN_TOUR_FIRST_TIME, SHOW_GOTO_ADMIN_TOUR } from 'utils/constants'
import { DEFAULT_LOCALE, DRAWER_WIDTH, HOME_KEY } from 'utils/constants'
import { Privileges } from 'utils/rolesToPrivilegesMapper'
import Copilot from './copilot/Copilot'
import { GotoAdminTour } from './gotoAdminTour/GotoAdminTour'
import SideDrawer, { SideDrawerMode } from './layout/SideDrawer'
import { AppThemeContext } from './providers/AppThemeProvider'
import { UnleashContext } from './providers/UnleashProvider'
import ResolveUpgradeBanner from './ResolveUpgradeBanner'
import GuidedSetupSurveyDialog, { TriggerType } from './shared/GuidedSetupSurveyDialog'
import SwitchBackToOldAdminDialog from './shared/SwitchBackToOldAdminDialog'
import UpgradeNowDialog from './shared/UpgradeNowDialog'
const ErrorMessage = lazy(() => import('./ErrorMessage'))
import { emitChange } from '@ucc/admin-global-state'

import { CLOSE_SIDE_DRAWER_EVENT, OPEN_SIDE_DRAWER_EVENT, OpenSideDrawerPayload } from 'utils/messaging/events'
import { useAccountContext } from './contexts/AccountContext'
export enum SetupDrawerMode {
  setupConnect = 'setupConnect',
  setupResolve = 'setupResolve',
  none = 'none',
}

const useStyles = makeStyles((theme: Theme) => ({
  root: (props: { bannerMargin: boolean; isIntercomBannerShowing: boolean }) => ({
    marginTop: calculateMargin(props),
    display: 'grid',
    gridTemplateAreas: "'SmallSidebar Sidebar Main'",
    gridTemplateColumns: 'auto auto 1fr',
    gridTemplateRows: 'auto 1fr',
    minHeight: `calc(100vh - ${calculateBannerHeight(props)}px)`,
  }),
  main: (props: { bannerMargin: boolean; isIntercomBannerShowing: boolean }) => ({
    maxHeight: `calc(100vh - ${(props.isIntercomBannerShowing ? 50 : 0) + calculateBannerHeight(props)}px)`,
    position: 'relative',
    height: '100%',
    overflowX: 'auto',
    backgroundColor: tokens.backgroundColorDefault,
  }),
  content: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  skipToContent: {
    left: 8,
    position: 'fixed',
    transform: 'translateY(-9999px)',
    zIndex: 3000,
    '&:focus': {
      transform: 'translateY(-43px)',
    },
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: DRAWER_WIDTH,
  },
}))

interface LayoutProps {
  children: ReactNode
  loading?: boolean
}

const TOP_BANNER_OFFSET_HEIGHT = 42
const RESOLVE_BANNER_OFFSET_HEIGHT = 40

const Layout: FC<LayoutProps> = ({ children, loading }) => {
  const copilotPortalNode = useMemo(
    () => createHtmlPortalNode({ attributes: { id: 'copilot-drawer-portal', style: 'height: 100%' } }),
    [],
  )
  const { copilotAIEnabled } = useContext(UnleashContext)
  const { accountKey: selectedAccountKey, currentAccount } = useAccountContext()
  const locale = useLocale()
  const history = useHistory()
  const route = useRouteMatch()
  const pbx = useContext(OrganizationContext)
  const { hideSplashScreen } = useHideSplashScreen()

  const [isCopilotPin, setIsCopilotPin] = useLocalStorage(config.COPILOT_PINNED, true)
  const [isCopilotOpen, setIsCopilotOpen] = useState(false)
  const [selectedTab, setSelectedTab] = useState<string>('')
  const [isIntercomBannerShowing, setIsIntercomBannerShowing] = useState<boolean>(false)
  const [setupDrawerMode, setSetupDrawerMode] = useState<SetupDrawerMode>(SetupDrawerMode.none)
  const [lastSelectedSubTabs, setLastSelectedSubTabs] = useState(new Map())
  const [guidedSetupSurveyState, setGuidedSetupSurveyState] = useState<TriggerType>(TriggerType.None)
  const [error, setError] = useState<{ error: Error }>()
  const [dismissedBanners] = useLocalStorage<string[]>(config.DISMISSED_BANNERS, [])
  const activities = useActivities()
  const { topBanner } = useContext(TopBarBannerContext)
  const { banners, removeBanner } = useBanners()
  const hasBanner = !!banners.length || !!topBanner?.message

  const classes = useStyles({
    bannerMargin: hasBanner,
    isIntercomBannerShowing,
  })

  const listenedSideBarPath = [
    HOME_KEY,
    DEVICES_ROUTE,
    PLACES_ROUTE,
    PEOPLE_ROUTE,
    'inbox',
    SETTINGS_ROUTE,
    PHONES_ROUTE,
    BILLING_ROUTE,
    GTC_BILLING_ROUTE,
  ]
  const appTheme = useContext(AppThemeContext)
  const guidedSetupConfig = pbx?.guidedSetupConfig
  const dynamicSetupConfig = pbx?.dynamicSetupConfig
  const hasSetupPrivilege = usePrivilege(Privileges.Setup)
  const hasG2RSetupPrivilege = usePrivilege(Privileges.SetupG2R)
  const [hasClosedSetupTray, setHasClosedSetupTray] = useLocalStorage<boolean>(
    config.HAS_CLOSED_GUIDED_SETUP_ONCE,
    localStorage.getItem(config.HAS_CLOSED_GUIDED_SETUP_ONCE)?.toString() === 'true',
  )
  const [hasCompletedAllGuidedSetupStepsOnce, setHasCompletedAllGuidedSetupStepsOnce] = useLocalStorage<boolean>(
    config.HAS_COMPLETED_ALL_GUIDED_SETUP_STEPS_ONCE,
    localStorage.getItem(config.HAS_COMPLETED_ALL_GUIDED_SETUP_STEPS_ONCE)?.toString() === 'true',
  )
  const isDefaultLanguage = locale === DEFAULT_LOCALE

  const [sideDrawerState, setSideDrawerState] = useState(SideDrawerMode.none)
  const [customSideDrawerContent, setCustomSideDrawerContent] = useState<HTMLElement>()
  const { resolveBannerVisible, handleHideBanner } = useResolveUpgradeBanner()

  const resolveSetupDefaultClosed = localStorage.getItem(config.RESOLVE_GUIDED_SETUP_CLOSED)?.toString() === 'true'
  const [showGotoAdminTour] = useLocalStorage<boolean>(SHOW_GOTO_ADMIN_TOUR)
  const [showAdminTourFirstTime] = useLocalStorage<boolean>(SHOW_ADMIN_TOUR_FIRST_TIME, true)

  useEffect(hideSplashScreen, [hideSplashScreen])

  // Checking if intercom banner is showing... hacky I know
  const target = document.body
  const observer = new MutationObserver((mutations) => {
    mutations.forEach(({ target }: any) => {
      if (target.style['margin-top'] === '50px') {
        setIsIntercomBannerShowing(true)
      } else {
        setIsIntercomBannerShowing(false)
      }
    })
  })
  observer.observe(target, { attributes: true, attributeFilter: ['style'] })

  const onCloseSurveyDialog = () => {
    if (guidedSetupSurveyState === TriggerType.Exit) {
      setHasClosedSetupTray(true)
    } else if (guidedSetupSurveyState === TriggerType.SetupComplete) {
      setHasCompletedAllGuidedSetupStepsOnce(true)
    }
    setGuidedSetupSurveyState(TriggerType.None)
  }

  const closeSideDrawer = () => {
    if (sideDrawerState === SideDrawerMode.setup) {
      !hasClosedSetupTray &&
        isDefaultLanguage &&
        !hasCompletedAllGuidedSetupStepsOnce &&
        setGuidedSetupSurveyState(TriggerType.Exit)
    } else if (sideDrawerState === SideDrawerMode.setupResolve) {
      Analytics.track(mixpanelEventNames.RESOLVE_GUIDED_SETUP, {
        Action: 'Closed Resolve setup guide',
      })
    } else if (sideDrawerState === SideDrawerMode.copilot) {
      setIsCopilotOpen(false)
    }

    onUpdateSideDrawerMode(SideDrawerMode.none)
  }

  const onUpdateSideDrawerMode = useCallback((newSideDrawerMode: SideDrawerMode) => {
    emitChange((prev) => ({ ...prev, rightDrawerOpened: newSideDrawerMode !== SideDrawerMode.none }))
    setSideDrawerState(newSideDrawerMode)
    if (newSideDrawerMode !== SideDrawerMode.copilot && newSideDrawerMode !== SideDrawerMode.none) {
      setIsCopilotOpen(false)
    }
  }, [])

  const onPin = useCallback(
    (isPinned: boolean) => {
      // Popover flick is fixed by forcing it to be close before "moving" back Copilot in its unpined position
      onUpdateSideDrawerMode(SideDrawerMode.none)
      setIsCopilotOpen(false)
      setIsCopilotPin(false)

      setTimeout(() => {
        setIsCopilotOpen(true)
        setIsCopilotPin(isPinned)
        onUpdateSideDrawerMode(isPinned ? SideDrawerMode.copilot : SideDrawerMode.none)
      }, 0)
    },
    [onUpdateSideDrawerMode, setIsCopilotPin],
  )

  useEffect(() => {
    if (dynamicSetupConfig?.setupType === SetupType.DYNAMIC_SETUP && !!pbx?.showJive) {
      setSetupDrawerMode(SetupDrawerMode.none)
      return
    }

    const hasConnectGuidedSetup =
      (dynamicSetupConfig?.setupType === SetupType.GUIDED_SETUP || guidedSetupConfig?.hasGuidedSetup) && !!pbx?.showJive

    const microtransactionQuickFlow = new URLSearchParams(location.search).has('target_product')
    const hasResolveGuidedSetup = !!pbx?.showResolve && !microtransactionQuickFlow

    if (!hasConnectGuidedSetup && !hasResolveGuidedSetup) {
      onUpdateSideDrawerMode(SideDrawerMode.none)
      setSetupDrawerMode(SetupDrawerMode.none)
    } else if (hasConnectGuidedSetup) {
      setSetupDrawerMode(SetupDrawerMode.setupConnect)

      const setupConfig = dynamicSetupConfig || guidedSetupConfig || { shownOnLogIn: false }
      if (!setupConfig.shownOnLogIn) {
        onUpdateSideDrawerMode(SideDrawerMode.none)
      } else if (setupConfig.shownOnLogIn && sideDrawerState !== SideDrawerMode.setup && hasSetupPrivilege) {
        onUpdateSideDrawerMode(SideDrawerMode.setup)
      }
    } else if (hasResolveGuidedSetup && hasG2RSetupPrivilege) {
      setSetupDrawerMode(SetupDrawerMode.setupResolve)
      if (!resolveSetupDefaultClosed && sideDrawerState !== SideDrawerMode.setupResolve) {
        onUpdateSideDrawerMode(SideDrawerMode.setupResolve)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pbx?.lmiAccountKey,
    guidedSetupConfig?.hasGuidedSetup,
    guidedSetupConfig?.shownOnLogIn,
    dynamicSetupConfig?.setupType,
    dynamicSetupConfig?.shownOnLogIn,
    pbx?.showJive,
    pbx?.showResolve,
  ])

  useEffect(() => {
    const topBannerOffset = topBanner ? TOP_BANNER_OFFSET_HEIGHT : 0
    const resolveBannerOffset = resolveBannerVisible ? RESOLVE_BANNER_OFFSET_HEIGHT : 0
    const totalOffset = topBannerOffset + resolveBannerOffset
    emitChange((prev) => ({ ...prev, bannerOffset: totalOffset }))
  }, [dismissedBanners.length, resolveBannerVisible, topBanner])

  useMessaging('guidedSetupAllStepsCompleted', (payload: unknown) => {
    if (payload && isDefaultLanguage) {
      onUpdateSideDrawerMode(SideDrawerMode.none)

      if (!hasCompletedAllGuidedSetupStepsOnce) {
        setGuidedSetupSurveyState(TriggerType.SetupComplete)
      }
    }
  })

  useMessaging(OPEN_SIDE_DRAWER_EVENT, (payload: unknown) => {
    const { content } = payload as OpenSideDrawerPayload
    setSideDrawerState(SideDrawerMode.custom)
    setCustomSideDrawerContent(content)
  })

  useMessaging(CLOSE_SIDE_DRAWER_EVENT, () => closeSideDrawer())

  useEffectOnce(() => {
    history.listen((e) => {
      // Listening to history changes in case we get redirected to a route
      // outside of the wrapper's knowledge
      // we still need to set the proper tab on the small sidebar
      const firstPath = e.pathname.split('/').filter((n) => n)[1]

      if (firstPath && selectedTab !== firstPath) {
        if (listenedSideBarPath.includes(firstPath)) {
          // TODO: for now only check on places, but down the road should check everywhere
          setSelectedTab(firstPath)
        }
      }

      if (error) {
        // Reset errors on route change
        setError(undefined)
      }
    })
  })

  const updateSelectedSubTabHistory = (selectedTab: string, selectedSubTab: string): void => {
    setLastSelectedSubTabs(new Map(lastSelectedSubTabs.set(selectedTab, selectedSubTab)))
  }

  const handleSmallSidebarLinkClick = (to: string, links: SidebarLink[]): void => {
    setSelectedTab(to)
    redirectToLastSelectedSubTab(to, links)
  }

  const findSelectedTabMatch = (
    route: string[] | undefined,
    historySelectedSubTab: Map<string, string>,
    to: string,
  ): boolean => {
    if (!route) return false
    const routeMatch = route[0] !== undefined && route[0] === historySelectedSubTab.get(to)
    const subRouteMatch = route[1] !== undefined && route[1] === historySelectedSubTab.get(to)
    return routeMatch || subRouteMatch
  }

  const redirectToLastSelectedSubTab = (to: string, links: SidebarLink[]): void => {
    if (!links) return
    let subTabIndex = 0
    let subTabSubOptionIndex = -1

    for (let i = 0; i < links.length; i++) {
      const route = links[i].to?.split('/').filter((n) => n)
      const match = findSelectedTabMatch(route, lastSelectedSubTabs, to)

      if (match) {
        subTabIndex = i
        break
      }

      const subOptionsLength = links[i]?.subOptions?.length ?? 0
      const hasSubOptions = subOptionsLength > 0
      if (hasSubOptions) {
        for (let j = 0; j < subOptionsLength; j++) {
          const subOptions = links[i].subOptions
          if (subOptions !== undefined) {
            const subOptionsRoute = subOptions[j].to?.split('/').filter((n) => n)
            const subOptionsMatch = findSelectedTabMatch(subOptionsRoute, lastSelectedSubTabs, to)
            if (subOptionsMatch) {
              subTabIndex = i
              subTabSubOptionIndex = j
              break
            }
          }
        }
      }
    }

    redirect(links, subTabIndex, subTabSubOptionIndex, route, history)
  }

  useEffect(() => {
    // Reset last selected sub tabs when changing account
    // This is because you might have previously selected a tab in an old account
    // that isn't available in the newly selected account
    setLastSelectedSubTabs(new Map())
  }, [selectedAccountKey])

  useMessaging('legalTermsUpdate', (payload: unknown) => {
    const legalTermsInfo = payload as { aiType: string; state: boolean }
    if (legalTermsInfo.aiType === 'copilot') {
      pbx?.onCopilotLegalTermsAgreements &&
        pbx?.onCopilotLegalTermsAgreements(
          legalTermsInfo.state ? CopilotLegalTermsStatus.ACCEPTED : CopilotLegalTermsStatus.DECLINED,
        )
      if (!legalTermsInfo.state) {
        setIsCopilotOpen(false)
        onUpdateSideDrawerMode(SideDrawerMode.none)
      }
    }
  })

  const showGotoAdminOverview = useMemo(() => {
    return (
      currentAccount?.unifiedAdmin && !!pbx?.switchFromExternalAdmin && (showAdminTourFirstTime || showGotoAdminTour)
    )
  }, [currentAccount?.unifiedAdmin, pbx?.switchFromExternalAdmin, showAdminTourFirstTime, showGotoAdminTour])

  return (
    <Box className={clsx(classes.content, { [classes.contentShift]: sideDrawerState !== SideDrawerMode.none })}>
      {copilotAIEnabled && pbx?.copilotLegalTerms && (
        <InPortal node={copilotPortalNode}>
          <Copilot isPin={isCopilotPin} onPin={onPin} copilotLegalTerms={pbx.copilotLegalTerms} />
        </InPortal>
      )}
      <AdminBanners banners={banners} removeBanner={removeBanner} />

      <TopBar
        setupDrawerMode={setupDrawerMode}
        sideDrawerState={sideDrawerState}
        onUpdateSideDrawerMode={onUpdateSideDrawerMode}
        chameleonTheme={appTheme.chameleonTheme}
        activities={activities}
        onSelect={setSelectedTab}
        hasBanner={isIntercomBannerShowing}
        onSelectSubTab={updateSelectedSubTabHistory}
        dynamicSetupConfig={dynamicSetupConfig}
        copilotPortalNode={copilotPortalNode}
        isCopilotPin={isCopilotPin}
        isCopilotOpen={isCopilotOpen}
        onCopilotClick={setIsCopilotOpen}
      />

      <UpgradeNowDialog
        onClose={() => pbx?.setShowUpgradeDialog(false)}
        pbx={pbx}
        open={pbx?.showUpgradeDialog || false}
      />

      <Box className={classes.root}>
        <SmallSidebar onSelect={handleSmallSidebarLinkClick} selectedTab={selectedTab} bannerMargin={hasBanner} />
        <Sidebar
          onSelect={setSelectedTab}
          onSelectSubTab={updateSelectedSubTabHistory}
          selectedTab={selectedTab}
          bannerMargin={hasBanner}
        />
        <Box id="main" component="main" role="main" className={classes.main}>
          {error && (
            <Suspense fallback={<></>}>
              <ErrorMessage error={error} />
            </Suspense>
          )}

          {/* Only show loader if on an external sub-app */}
          {loading && <TopPageLoader />}

          {!loading && resolveBannerVisible && <ResolveUpgradeBanner handleHideBanner={handleHideBanner} />}
          <div
            id="subapp-wrapper"
            style={{
              height: '100%',
              width: '100%',
            }}
          >
            {children}
          </div>
        </Box>
      </Box>

      {/* Qualtrics survey dialog */}
      <GuidedSetupSurveyDialog onClose={onCloseSurveyDialog} pbx={pbx} triggerType={guidedSetupSurveyState} />

      <SwitchBackToOldAdminDialog
        onClose={() => pbx?.setShowSwitchBackDialog(false)}
        pbx={pbx}
        open={pbx?.showSwitchBackDialog || false}
      />

      <SideDrawer
        activities={activities}
        hasBanner={hasBanner}
        bannerOffset={TOP_BANNER_OFFSET_HEIGHT}
        sideDrawerMode={sideDrawerState}
        onCloseDrawer={closeSideDrawer}
        copilotPortalNode={copilotPortalNode}
        customSideDrawerContent={customSideDrawerContent}
      />

      {showGotoAdminOverview && <GotoAdminTour />}
    </Box>
  )
}

export default memo(Layout)
